import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Target, Salad, ArrowRight, CheckCircle2, Plus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

const HEALTH_CONDITIONS = [
  // Enfermedades metabólicas y cardiovasculares
  { id: 'obesidad', label: 'Obesidad', icon: '⚖️' },
  { id: 'sindrome_metabolico', label: 'Síndrome metabólico', icon: '🫀' },
  { id: 'aterosclerosis', label: 'Aterosclerosis', icon: '🫀' },
  { id: 'enfermedad_coronaria', label: 'Enfermedad coronaria', icon: '❤️' },
  { id: 'acv', label: 'ACV', icon: '🧠' },
  { id: 'higado_graso', label: 'Hígado graso', icon: '🫀' },
  { id: 'trigliceridos_altos', label: 'Triglicéridos altos', icon: '🫀' },
  { id: 'colesterol_alto', label: 'Colesterol alto', icon: '🫀' },
  // Enfermedades relacionadas con el azúcar
  { id: 'diabetes', label: 'Diabetes tipo 2', icon: '🩸' },
  { id: 'resistencia_insulina', label: 'Resistencia a la insulina', icon: '🍬' },
  { id: 'hipoglucemia', label: 'Hipoglucemia reactiva', icon: '🍬' },
  { id: 'caries', label: 'Caries dental', icon: '🦷' },
  // Enfermedades asociadas al sodio
  { id: 'hipertension', label: 'Hipertensión arterial', icon: '❤️' },
  { id: 'enfermedad_renal', label: 'Enfermedad renal crónica', icon: '🧂' },
  { id: 'retencion_liquidos', label: 'Retención de líquidos', icon: '💧' },
  { id: 'insuficiencia_cardiaca', label: 'Insuficiencia cardíaca', icon: '❤️' },
  // Trastornos digestivos
  { id: 'gastritis', label: 'Gastritis', icon: '🍔' },
  { id: 'reflujo', label: 'Reflujo gastroesofágico', icon: '🍔' },
  { id: 'colon_irritable', label: 'Colon irritable', icon: '🍔' },
  { id: 'estrenimiento', label: 'Estreñimiento crónico', icon: '🍔' },
  { id: 'diarrea_cronica', label: 'Diarrea crónica', icon: '🍔' },
  { id: 'diverticulosis', label: 'Diverticulosis', icon: '🍔' },
  // Intolerancias y alergias
  { id: 'celiaco', label: 'Enfermedad celíaca', icon: '🌾' },
  { id: 'intolerancia_lactosa', label: 'Intolerancia a la lactosa', icon: '🥛' },
  { id: 'alergia_alimentaria', label: 'Alergia alimentaria', icon: '🌾' },
  { id: 'sensibilidad_gluten', label: 'Sensibilidad al gluten', icon: '🌾' },
  // Enfermedades inflamatorias
  { id: 'inflamacion_cronica', label: 'Inflamación crónica', icon: '🧠' },
  { id: 'gota', label: 'Gota', icon: '🦴' },
  { id: 'artritis', label: 'Artritis', icon: '🦴' },
  { id: 'osteoporosis', label: 'Osteoporosis', icon: '🦴' },
  // Deficiencias nutricionales
  { id: 'anemia', label: 'Anemia', icon: '🧬' },
  { id: 'deficit_vitaminaB12', label: 'Déficit de vitamina B12', icon: '🧬' },
  { id: 'deficit_vitaminaD', label: 'Déficit de vitamina D', icon: '🧬' },
  { id: 'desnutricion', label: 'Desnutrición', icon: '🧬' },
  // Cánceres asociados
  { id: 'cancer_colon', label: 'Cáncer de colon', icon: '🎗️' },
  { id: 'cancer_gastrico', label: 'Cáncer gástrico', icon: '🎗️' },
  { id: 'cancer_higado', label: 'Cáncer de hígado', icon: '🎗️' },
  { id: 'cancer_pancreas', label: 'Cáncer de páncreas', icon: '🎗️' },
  { id: 'ninguna', label: 'Ninguna', icon: '✨' },
];

const HEALTH_GOALS = [
  { id: 'reducir_azucar', label: 'Reducir azúcar', icon: '🍬' },
  { id: 'reducir_sodio', label: 'Reducir sodio', icon: '🧂' },
  { id: 'reducir_grasas', label: 'Reducir grasas', icon: '🥓' },
  { id: 'bajar_peso', label: 'Bajar de peso', icon: '📉' },
  { id: 'comer_saludable', label: 'Comer más saludable', icon: '🥗' },
];

const DIETARY_PREFERENCES = [
  { id: 'vegano', label: 'Vegano', icon: '🌱' },
  { id: 'vegetariano', label: 'Vegetariano', icon: '🥕' },
  { id: 'ovolactovegetariano', label: 'Ovolactovegetariano', icon: '🥚🥛' },
  { id: 'ovovegetariano', label: 'Ovovegetariano', icon: '🥚' },
  { id: 'lactovegetariano', label: 'Lactovegetariano', icon: '🥛' },
  { id: 'sin_tacc', label: 'Sin TACC', icon: '🚫🌾' },
  { id: 'sin_lactosa', label: 'Sin lactosa', icon: '🚫🥛' },
  { id: 'ninguna', label: 'Ninguna', icon: '✨' },
];

export default function HealthProfile({ initialData, onComplete, onSkip }) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    health_conditions: initialData?.health_conditions || [],
    custom_health_conditions: initialData?.custom_health_conditions || [],
    health_goals: initialData?.health_goals || [],
    dietary_preferences: initialData?.dietary_preferences || [],
  });
  const [customCondition, setCustomCondition] = useState('');

  const toggleItem = (category, itemId) => {
    setProfile(prev => {
      const items = prev[category];
      
      // Si selecciona "ninguna", limpia todo
      if (itemId === 'ninguna') {
        return { ...prev, [category]: items.includes('ninguna') ? [] : ['ninguna'] };
      }
      
      // Si selecciona algo y "ninguna" está activo, quita "ninguna"
      const filtered = items.filter(id => id !== 'ninguna');
      
      if (filtered.includes(itemId)) {
        return { ...prev, [category]: filtered.filter(id => id !== itemId) };
      } else {
        return { ...prev, [category]: [...filtered, itemId] };
      }
    });
  };

  const addCustomCondition = () => {
    if (customCondition.trim()) {
      setProfile(prev => ({
        ...prev,
        custom_health_conditions: [...prev.custom_health_conditions, customCondition.trim()]
      }));
      setCustomCondition('');
    }
  };

  const removeCustomCondition = (index) => {
    setProfile(prev => ({
      ...prev,
      custom_health_conditions: prev.custom_health_conditions.filter((_, i) => i !== index)
    }));
  };

  const canContinue = () => {
    if (step === 1) return profile.health_conditions.length > 0 || profile.custom_health_conditions.length > 0;
    if (step === 2) return profile.health_goals.length > 0;
    if (step === 3) return profile.dietary_preferences.length > 0;
    return false;
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      onComplete(profile);
    }
  };

  const OptionCard = ({ item, category, selected }) => (
    <motion.div
      whileTap={{ scale: 0.97 }}
      onClick={() => toggleItem(category, item.id)}
      className={`
        relative p-4 rounded-xl border-2 cursor-pointer transition-all
        ${selected 
          ? 'border-emerald-500 bg-emerald-50' 
          : 'border-gray-200 bg-white hover:border-gray-300'
        }
      `}
    >
      {selected && (
        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
          <CheckCircle2 className="w-4 h-4 text-white" />
        </div>
      )}
      <div className="text-2xl mb-2">{item.icon}</div>
      <p className={`text-sm font-medium ${selected ? 'text-emerald-900' : 'text-gray-700'}`}>
        {item.label}
      </p>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Paso {step} de 3</span>
            <span className="text-sm font-medium text-emerald-600">{Math.round((step / 3) * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
              initial={{ width: 0 }}
              animate={{ width: `${(step / 3) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <Card className="p-8 shadow-lg border-0">
          {/* Step 1: Condiciones de salud */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tu salud</h2>
                  <p className="text-gray-500">¿Tenés alguna condición médica?</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {HEALTH_CONDITIONS.map(item => (
                  <OptionCard
                    key={item.id}
                    item={item}
                    category="health_conditions"
                    selected={profile.health_conditions.includes(item.id)}
                  />
                ))}
              </div>

              <p className="text-xs text-gray-400 text-center mb-4">
                Podés seleccionar varias opciones
              </p>

              {/* Custom Condition Input */}
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="text-sm font-medium text-gray-700 mb-3">O escribe una condición personalizada:</p>
                <div className="flex gap-2">
                  <Input
                    value={customCondition}
                    onChange={(e) => setCustomCondition(e.target.value)}
                    placeholder="Ej: Intolerancia a la lactosa"
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && addCustomCondition()}
                  />
                  <Button
                    type="button"
                    onClick={addCustomCondition}
                    disabled={!customCondition.trim()}
                    size="icon"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {profile.custom_health_conditions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {profile.custom_health_conditions.map((condition, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-sm"
                      >
                        <span>{condition}</span>
                        <button
                          type="button"
                          onClick={() => removeCustomCondition(index)}
                          className="hover:text-emerald-900"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 2: Objetivos */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tus objetivos</h2>
                  <p className="text-gray-500">¿Qué querés lograr?</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {HEALTH_GOALS.map(item => (
                  <OptionCard
                    key={item.id}
                    item={item}
                    category="health_goals"
                    selected={profile.health_goals.includes(item.id)}
                  />
                ))}
              </div>

              <p className="text-xs text-gray-400 text-center">
                Podés seleccionar varias opciones
              </p>
            </motion.div>
          )}

          {/* Step 3: Preferencias */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Salad className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tus preferencias</h2>
                  <p className="text-gray-500">¿Seguís alguna dieta especial?</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {DIETARY_PREFERENCES.map(item => (
                  <OptionCard
                    key={item.id}
                    item={item}
                    category="dietary_preferences"
                    selected={profile.dietary_preferences.includes(item.id)}
                  />
                ))}
              </div>

              <p className="text-xs text-gray-400 text-center">
                Podés seleccionar varias opciones
              </p>
            </motion.div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button
                onClick={() => setStep(step - 1)}
                variant="outline"
                className="flex-1"
              >
                Atrás
              </Button>
            )}
            {step === 1 && onSkip && (
              <Button
                onClick={onSkip}
                variant="ghost"
                className="text-gray-400 text-sm"
              >
                Omitir
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canContinue()}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              {step === 3 ? 'Finalizar' : 'Continuar'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}